/**
 * Générateur — Il résout des problèmes utilisant les conversions d'unités sur
 * des grandeurs composées.
 * Attendu ID : 234 | Niveau : 3ème | Domaine : Grandeurs (d18, O4)
 *
 * Source doc prof (CS2 + Créé n°12). Plan auto-validé 2026-07-17 (TODO §6) :
 * « durees » (décimal ↔ sexagésimal — l'erreur 7 h 60 garantie), « vitesses »
 * (km/h ↔ m/s : ÷3,6 et ×3,6, l'automatisme central), « debits » (m³/h ↔ L/s :
 * numérateur ET dénominateur), « reaction » (le temps de réaction du
 * référentiel : distance à v km/h pendant 1 s). Facteurs EXACTS uniquement
 * (leçon 4e : jamais d'arrondi litigieux dans une conversion).
 */

import { randomChoice, randInt, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 234;
const L_DUREES = 'Convertir des durées (décimal ↔ heures-minutes)';
const L_VITESSES = 'Convertir des vitesses (km/h ↔ m/s)';
const L_DEBITS = 'Convertir des débits (m³/h ↔ L/s)';
const L_REACTION = 'La distance parcourue pendant le temps de réaction';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(Math.round(x * 1000) / 1000).replace('.', ',');

// ═════════ « durees » ═════════

const genDureesTrous = () => {
    // Décimal → h min : h,d avec d ∈ {1..9} → minutes = 6d ; et l'inverse.
    const h1 = randInt(1, 9); const d1 = randInt(1, 9);
    const min1 = 6 * d1;
    const min2 = randomChoice([15, 30, 45, 12, 18, 24, 36, 48]);
    const h2 = randInt(0, 5);
    const dec2 = frNb(h2 + min2 / 60);
    const phrase = `$${h1}{,}${d1}\\ \\text{h} = {{${h1}}}\\ \\text{h}\\ {{${min1}}}\\ \\text{min}`
        + ` \\qquad ${h2}\\ \\text{h}\\ ${min2}\\ \\text{min} = {{${dec2}}}\\ \\text{h}$`;
    const payload = makeTexteTrou(phrase,
        // Le « Piège : 3,7 h n'est PAS 3 h 7 min » nommait l'erreur à éviter.
        '<p>Rappel : une heure fait 60 minutes.</p>'
        + '<p><b>Complète les conversions de durées.</b></p>',
        { ...opts(L_DUREES), mode: 'saisie' });
    payload._meta.params = { h1, d1, min1, h2, min2, dec2 };
    return payload;
};

// ═════════ « vitesses » — ÷3,6 / ×3,6 exacts ═════════

const genVitessesTrous = () => {
    // km/h → m/s exact : v multiple de 1,8 → v/3,6 fini court. On tire v = 18k/10…
    const kmh = randomChoice([18, 36, 54, 72, 90, 99, 108, 126, 130 - 4]);   // 126 = 35 m/s…
    const ms1 = frNb(kmh / 3.6);
    const ms2 = randomChoice([5, 12.5, 15, 17.5, 20, 25, 30]);
    const kmh2 = frNb(ms2 * 3.6);
    const phrase = `$${kmh}\\ \\text{km/h} = {{${ms1}}}\\ \\text{m/s}`
        + ` \\qquad ${frNb(ms2).replace(',', '{,}')}\\ \\text{m/s} = {{${kmh2}}}\\ \\text{km/h}$`;
    const payload = makeTexteTrou(phrase,
        // Les deux opérations (÷ 3,6 et × 3,6) SONT la conversion à connaître.
        '<p><b>Complète les conversions de vitesses.</b> Utilise une virgule pour les décimaux.</p>',
        { ...opts(L_VITESSES), mode: 'saisie' });
    payload._meta.params = { kmh, ms2 };
    return payload;
};

// ═════════ « debits » — m³/h ↔ L/s ═════════

const genDebitsTrous = () => {
    // m³/h → L/s : ×1000 ÷3600 = ÷3,6 → mêmes multiples exacts.
    const m3h = randomChoice([9, 18, 27, 36, 45, 54, 72, 90]);
    const ls1 = frNb(m3h / 3.6);
    const ls2 = randomChoice([1.5, 2, 2.5, 5, 7.5, 10]);
    const m3h2 = frNb(ls2 * 3.6);
    const phrase = `$${m3h}\\ \\text{m}^{3}\\text{/h} = {{${ls1}}}\\ \\text{L/s}`
        + ` \\qquad ${frNb(ls2).replace(',', '{,}')}\\ \\text{L/s} = {{${m3h2}}}\\ \\text{m}^{3}\\text{/h}$`;
    const payload = makeTexteTrou(phrase,
        // Seules les équivalences d'unités restent : « revient à ÷ 3,6 » était
        // la conversion à établir.
        '<p>Rappel : 1 m³ = 1 000 L et 1 h = 3 600 s.</p>'
        + '<p><b>Complète les conversions de débits.</b></p>',
        { ...opts(L_DEBITS), mode: 'saisie' });
    payload._meta.params = { m3h, ls2 };
    return payload;
};

// ═════════ « reaction » — le Créé n°12 ═════════

const genReactionTrous = () => {
    // d (m) parcourue en 1 s à v km/h = v/3,6 — v multiple de 1,8 → EXACT.
    const v = randomChoice([36, 54, 72, 90, 108, 126]);
    const d = frNb(v / 3.6);
    const v2 = randomChoice([18, 45, 63, 81, 99].filter(x => x !== v));
    const d2 = frNb(v2 / 3.6);
    const phrase = `$\\text{À } ${v}\\ \\text{km/h : } {{${d}}}\\ \\text{m}`
        + ` \\qquad \\text{à } ${v2}\\ \\text{km/h : } {{${d2}}}\\ \\text{m}$`;
    const payload = makeTexteTrou(phrase,
        '<p>Un conducteur met 1 seconde avant de commencer à freiner quand il voit un obstacle.</p>'
        // « Convertis d'abord en m/s » et « PROPORTIONNELLE » livraient la
        // méthode et la conclusion.
        + '<p><b>Quelle distance parcourt-il pendant cette seconde, aux deux vitesses '
        + 'proposées ?</b></p>',
        { ...opts(L_REACTION), mode: 'saisie' });
    payload._meta.params = { v, v2 };
    return payload;
};

const GENERATORS = {
    durees_texte_trou_saisie: genDureesTrous,
    vitesses_texte_trou_saisie: genVitessesTrous,
    debits_texte_trou_saisie: genDebitsTrous,
    reaction_texte_trou_saisie: genReactionTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'durees', label: L_DUREES, formatsList: buildFormats('durees') },
    { id: 'vitesses', label: L_VITESSES, formatsList: buildFormats('vitesses') },
    { id: 'debits', label: L_DEBITS, formatsList: buildFormats('debits') },
    { id: 'reaction', label: L_REACTION, formatsList: buildFormats('reaction') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
